import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Avatar from '@mui/material/Avatar';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import { formatDistanceToNow } from 'date-fns';
import { CloseOutlined, EditOutlined, PublicOffOutlined, PublicOutlined } from '@mui/icons-material';
import { Box, Collapse, Divider, Tooltip } from '@mui/material';
import { Chip, Typography } from '@sm-dashboard/ui-components';
import { getUser } from '@/util/getUser';
import { Post, Comment } from '@sm-dashboard/types';
import { useState, useEffect, type MouseEvent } from 'react';
import instanceAxios from '@/util/axios';
import { copyToClipboard } from '@/util/commonFunction';
import { deletePostUrl, likeOrLikePostUrl, updatePostUrl } from '@/constants';
import DynamicDialog from '../DaynmicDialog';
import { useAppContext } from '@/context/context';
import malashLogo from '../../../assets/4.jpg';
import heLocale from 'date-fns/locale/he';
import { EditorComponent } from '@sm-dashboard/form-provider';
import { Button, Dialog, IconButton } from '@sm-dashboard/ui-components';
import { theme } from '@/theme/theme';
import ShareIcon from '@mui/icons-material/Share';
import CommentIcon from '@mui/icons-material/Comment';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import FeedComments from './FeedComments';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { checkFolderPermissions } from '../Archive/ArchiveTags';
import FeedTagsAllow from './FeedTags';

export default function FeedCard({ post, refreshFeed }: { post: Post; refreshFeed: () => Promise<void> }) {
  const {
    id,
    department,
    content,
    createdAt,
    updatedAt,
    FeedLikes,
    FeedComments: initialPostComments,
    category,
    isPublishApproved,
    Shluchim
  } = post;
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editContent, setEditContent] = useState<string | undefined>(content);
  const { setSnackbar, userPermission, shaliach } = useAppContext();
  const [loading, setLoading] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [postComments, setPostComments] = useState<Comment[]>(initialPostComments);
  const [publishApproved, setPublishApproved] = useState(isPublishApproved);
  const [openTagsDialog, setOpenTagsDialog] = useState(false);
  const [tagsAllow, setTagsAllow] = useState<string[]>(post.tagsAllow || []);

  const formattedTime = formatDistanceToNow(new Date(createdAt ?? updatedAt), {
    locale: heLocale,
    addSuffix: true
  });
  const isUserLiked = shaliach && FeedLikes.find(like => like.shaliachId === shaliach.id)?.shaliachId ? true : false;

  const [like, setLike] = useState({ isLike: isUserLiked, likes: FeedLikes.length });

  const updatePost = async () => {
    setLoading(true);
    await instanceAxios.post(`${updatePostUrl}/${post.id}`, {
      content: editContent,
      department,
      tagsAllow: tagsAllow
    });
    await refreshFeed();
    setLoading(false);
    setOpenEditDialog(false);
    setSnackbar({ message: 'פוסט עודכן בהצלחה', severity: 'success', open: true });
  };

  const updatePublishApproved = async () => {
    await instanceAxios.post(`${updatePostUrl}/${post.id}`, {
      isPublishApproved: !publishApproved
    });
    setPublishApproved(!publishApproved);
    setSnackbar({ message: 'פוסט עודכן בהצלחה', severity: 'success', open: true });
  };

  const deletePost = async () => {
    await instanceAxios.delete(`${deletePostUrl}/${post.id}`);
    setOpenDeleteDialog(false);
    await refreshFeed();
    setSnackbar({ message: 'פוסט נמחק בהצלחה', severity: 'success', open: true });
  };

  const unDeletePost = async () => {
    setOpenDeleteDialog(false);
  };

  const likeOrUnlikePost = async () => {
    setLike({ isLike: !like.isLike, likes: like.isLike ? like.likes - 1 : like.likes + 1 });
    await instanceAxios.post(likeOrLikePostUrl, { isLikePost: !like.isLike, id: post.id });
    post.FeedLikes.find(like => like.shaliachId === shaliach?.id)
      ? post.FeedLikes.pop()
      : post.FeedLikes.push({ shaliachId: shaliach?.id || 1, postId: post.id, id: -99 });
  };

  const copyButton = (postId: number) => {
    copyToClipboard(`${window.location.origin}/org/feed/${postId}`, setSnackbar, 'קישור לפוסט הועתק ללוח');
  };

  const handleShowComments = () => {
    setShowCommentInput(false);
    setShowComments(prev => !prev);
  };

  const handleToggleCommentInput = (event: MouseEvent) => {
    event.stopPropagation();
    setShowComments(false);
    setShowCommentInput(prev => !prev);
  };

  const addComment = (newComment: Comment) => {
    setPostComments(prevComments => [...prevComments, newComment]);
  };

  const deleteComment = (commentId: number) => {
    setPostComments(prevComments => prevComments.filter(comment => comment.id !== commentId));
  };

  const updatePostTags = async (selectedTags: string[]) => {
    try {
      await instanceAxios.post(`${updatePostUrl}/${post.id}`, {
        tagsAllow: selectedTags
      });
      setTagsAllow(selectedTags);
      setSnackbar({ message: 'הרשאות הפוסט עודכנו בהצלחה', severity: 'success', open: true });
      return Promise.resolve();
    } catch (error) {
      console.error('שגיאה בעדכון תגיות:', error);
      setSnackbar({ message: 'אירעה שגיאה בעדכון הרשאות הפוסט', severity: 'error', open: true });
      return Promise.reject(error);
    }
  };

  const hasPermissionToView = checkFolderPermissions(tagsAllow, userPermission);

  const canManagePermissions =
    userPermission?.isAdmin || userPermission?.isSuperAdmin || userPermission.hasTag('עורך חדשות');

  return (
    <Card
      sx={{
        bgcolor: 'background.default',
        width: '100%',
        mt: 3,
        mx: { xs: 0, sm: 'auto' },
        borderRadius: { xs: 0, sm: 1 },
        maxWidth: { xs: '100vw', sm: '100%' },
        '& .html_content': {
          '& a': {
            display: 'inline-block',
            maxWidth: '100%'
          },
          '& a:has(img)': {
            display: 'block',
            position: { xs: 'relative', sm: 'static' },
            left: { xs: '50%', sm: 'auto' },
            transform: { xs: 'translateX(-50%)', sm: 'none' },
            width: { xs: 'calc(100% + 32px)', sm: '100%' },
            maxWidth: { xs: 'calc(100% + 32px)', sm: '100%' }
          },
          '& img': {
            height: 'auto',
            display: 'block',
            pointerEvents: 'none',
            WebkitUserDrag: 'none',
            width: '100%',
            maxWidth: '100%'
          },
          '& :not(a) > img': {
            position: { xs: 'relative', sm: 'static' },
            left: { xs: '50%', sm: 'auto' },
            transform: { xs: 'translateX(-50%)', sm: 'none' },
            width: { xs: 'calc(100% + 32px)', sm: '100%' },
            maxWidth: { xs: 'calc(100% + 32px)', sm: '100%' }
          },
          '& p': {
            padding: { xs: '0', sm: 0 }
          }
        }
      }}
      key={id}
    >
      <CardHeader
        sx={{
          bgcolor: theme.palette.grey.A100,
          py: 0.7,
          '& .MuiCardHeader-action': {
            margin: 0,
            alignSelf: 'center',
            display: 'flex',
            alignItems: 'center',
            height: '100%'
          }
        }}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {(userPermission?.isAdmin || userPermission?.isSuperAdmin || post.Shluchim?.id === shaliach?.id) && (
              <>
                <IconButton
                  variant="text"
                  color="secondary"
                  shape="circle"
                  size="medium"
                  aria-label="עריכה"
                  onClick={() => setOpenEditDialog(!openEditDialog)}
                >
                  <EditOutlined fontSize="small" />
                </IconButton>
                <IconButton
                  variant="text"
                  color="secondary"
                  shape="circle"
                  size="medium"
                  aria-label="מחיקה"
                  onClick={() => setOpenDeleteDialog(true)}
                >
                  <CloseOutlined fontSize="small" />
                </IconButton>

                {canManagePermissions && (
                  <Tooltip
                    title={tagsAllow.length > 0 ? `הרשאות נדרשות: ${tagsAllow.join(', ')}` : 'הגדרת הרשאות צפייה'}
                  >
                    <IconButton
                      variant="text"
                      color="secondary"
                      shape="circle"
                      size="medium"
                      aria-label="הגדרת הרשאות צפייה"
                      onClick={() => setOpenTagsDialog(true)}
                    >
                      {tagsAllow.length > 0 ? (
                        <LockIcon fontSize="small" color={hasPermissionToView ? 'primary' : 'error'} />
                      ) : (
                        <LockOpenIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}

            <Tooltip title="העתק קישור">
              <IconButton
                variant="text"
                color="secondary"
                shape="circle"
                size="medium"
                aria-label="העתק קישור"
                onClick={() => {
                  copyButton(post.id);
                }}
              >
                <ShareIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {(userPermission?.isAdmin || userPermission?.isSuperAdmin || userPermission.hasTag('עורך חדשות')) &&
              !department && (
                <Tooltip title={publishApproved ? 'ביטול פרסום פוסט' : 'אישור פוסט'}>
                  <IconButton
                    variant="text"
                    color="secondary"
                    shape="circle"
                    size="medium"
                    aria-label={publishApproved ? 'ביטול פרסום פוסט' : 'אישור פוסט'}
                    onClick={updatePublishApproved}
                  >
                    {publishApproved ? <PublicOffOutlined fontSize="small" /> : <PublicOutlined fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}
          </Box>
        }
        avatar={
          <Avatar
            src={department ? malashLogo : Shluchim?.profileImage || undefined}
            sx={{ backgroundColor: 'secondary.main', width: 32, height: 32 }}
          >
            {Shluchim?.firstName && Shluchim?.lastName
              ? `${Shluchim.firstName.charAt(0)}${Shluchim.lastName.charAt(0)}`
              : 'A'}
          </Avatar>
        }
        title={
          <Typography variant="subtitle2">
            {department
              ? department
              : Shluchim?.firstName && Shluchim?.lastName
              ? `הרב ${Shluchim.firstName} ${Shluchim.lastName}`
              : ''}
          </Typography>
        }
        subheader={<Typography variant="caption">{formattedTime}</Typography>}
      />
      <Divider />
      <CardContent sx={{ p: { xs: 2, sm: 2 }, overflow: 'hidden' }}>
        {!publishApproved && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Chip label={'ממתין לאישור'} variant={'warning'} />
          </Box>
        )}
        {openEditDialog ? (
          <>
            {canManagePermissions && (
              <Box sx={{ mt: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  startIcon={tagsAllow.length > 0 ? <LockIcon /> : <LockOpenIcon />}
                  onClick={() => setOpenTagsDialog(true)}
                  variant="outlined"
                  size="small"
                >
                  {tagsAllow.length > 0 ? `הרשאות צפייה (${tagsAllow.length})` : 'הגדרת הרשאות צפייה'}
                </Button>
              </Box>
            )}
            <EditorComponent height={600} value={editContent} setValue={setEditContent} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2 }}>
              <Button loading={loading} color="secondary" disabled={!content} onClick={updatePost}>
                שמור
              </Button>
              <Button variant="outlined" onClick={() => setOpenEditDialog(false)}>
                ביטול
              </Button>
            </Box>
          </>
        ) : (
          <div
            dangerouslySetInnerHTML={{ __html: content }}
            className="html_content"
            style={{ fontSize: '0.9rem' }}
          ></div>
        )}
      </CardContent>
      <Divider />
      <CardActions sx={{ py: 0 }}>
        {like.likes}
        <IconButton
          variant="text"
          color={like.isLike ? 'info' : 'secondary'}
          shape="circle"
          aria-label="לייק"
          onClick={likeOrUnlikePost}
        >
          {like.isLike ? <ThumbUpIcon color="info" /> : <ThumbUpOutlinedIcon />}
        </IconButton>
        <Typography variant="body2" color={like.isLike ? 'info.main' : 'inherit'}>
          לייק
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', gap: 0.5 }}>
          <Box
            onClick={handleShowComments}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
          >
            <Box sx={{ ml: 1 }}>{postComments.length}</Box>
            <IconButton variant="text" color="secondary" shape="circle" aria-label="תגובות">
              <CommentIcon />
            </IconButton>
            <Box sx={{ mr: 1 }}>תגובות</Box>
          </Box>
          <Box
            onClick={handleToggleCommentInput}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
          >
            <IconButton variant="text" color="secondary" shape="circle" aria-label="הוספת תגובה">
              <AddCommentOutlinedIcon />
            </IconButton>
            <Box sx={{ mr: 1 }}>הוספת תגובה</Box>
          </Box>
        </Box>
      </CardActions>

      <Collapse in={showComments || showCommentInput}>
        <FeedComments
          postId={id}
          CommentsList={postComments}
          showCommentsList={showComments}
          showCommentInput={showCommentInput}
          onCommentInputClose={() => setShowCommentInput(false)}
          CloseCommentSection={() => {
            setShowComments(false);
            setShowCommentInput(false);
          }}
          userImage={shaliach?.profileImage || undefined}
          addNewComment={addComment}
          deleteComment={deleteComment}
        />
      </Collapse>

      {canManagePermissions && (
        <Dialog
          open={openTagsDialog}
          onClose={() => setOpenTagsDialog(false)}
          maxWidth="sm"
          fullWidth
          title="הגדרת הרשאות צפייה בפוסט"
        >
          <FeedTagsAllow onClose={() => setOpenTagsDialog(false)} existingTags={tagsAllow} onSave={updatePostTags} />
        </Dialog>
      )}

      {openDeleteDialog && (
        <DynamicDialog
          open={openDeleteDialog}
          title=""
          subtitle="האם אתה בטוח שברצונך למחוק את הפוסט?"
          handleOkClose={deletePost}
          handleNotOkClose={unDeletePost}
        />
      )}
    </Card>
  );
}
